import { useEffect, useMemo, useRef, useState } from "react";
import type { AppEntry } from "../registry/apps";
import { isLive, isPlaceholder } from "../lib/progress";
import { appHref, formatDay, hostOf } from "../lib/format";

type Filter = "all" | "live" | "staged";

export function AppDirectory({ apps }: { apps: readonly AppEntry[] }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const t = event.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((app) => {
      if (filter === "live" && !isLive(app)) return false;
      if (filter === "staged" && !isPlaceholder(app)) return false;
      if (!q) return true;
      const blob = `${app.slug} ${app.title} ${app.url} ${app.blurb ?? ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [apps, query, filter]);

  const liveN = apps.filter(isLive).length;
  const stagedN = apps.filter(isPlaceholder).length;

  return (
    <section className="directory" id="inventory">
      <header className="dir-head">
        <div>
          <p className="dir-kicker">Inventory</p>
          <h2>Every registered app</h2>
          <p className="dir-sub">
            {apps.length} on the list · {liveN} live · {stagedN} staged examples. Live rows
            route to their subdomain. Examples stay here until they actually ship.
          </p>
        </div>

        <div className="dir-controls">
          <label className="find">
            <span>FIND</span>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="slug, title, host…"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <div className="filters" role="group" aria-label="Filter inventory">
            {(
              [
                ["all", "ALL"],
                ["live", "LIVE"],
                ["staged", "STAGED"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={filter === id ? "is-on" : ""}
                onClick={() => setFilter(id)}
                aria-pressed={filter === id}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="dir-table-wrap">
        <table className="dir-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Slug</th>
              <th scope="col">Title</th>
              <th scope="col">Status</th>
              <th scope="col">Shipped</th>
              <th scope="col">URL</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="dir-empty">
                  No apps match. Clear the search or switch filter.
                </td>
              </tr>
            ) : (
              filtered.map((app, i) => {
                const staged = isPlaceholder(app);
                const href = appHref(app.slug, app.url);
                const host = hostOf(href);
                return (
                  <tr key={app.slug} className={staged ? "is-staged" : ""}>
                    <td className="num">{String(i + 1).padStart(2, "0")}</td>
                    <td className="slug">{app.slug}</td>
                    <td>
                      <span className="dir-title">{app.title}</span>
                      {app.blurb ? <span className="dir-blurb">{app.blurb}</span> : null}
                    </td>
                    <td>
                      <span className={`pill ${staged ? "warn" : "ok"}`}>
                        {staged ? (app.example ? "EXAMPLE" : "WIP") : "LIVE"}
                      </span>
                    </td>
                    <td className="mono">{formatDay(app.shippedAt)}</td>
                    <td>
                      {staged ? (
                        <span className="dead-link">{host}</span>
                      ) : (
                        <a href={href}>{host}</a>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
